import React, { useEffect, useState } from 'react';
const storage: { [prop: string]: any } = {};
const possiblyParse = (input: string | null): undefined | unknown => {
    if (input !== null) {
        return JSON.parse(input);
    }
}

export interface userTokens {
    accessToken: { token: string, expiresIn: number };
    refreshTokens: string[];
    newToken?: true;
    refreshed?: true;
}

const dispatchStorageEvent = (key: string, newValue?: unknown, oldValue?: string) => {
    try {
        const serializedValue = newValue === undefined ? null : JSON.stringify(newValue);
        window.dispatchEvent(new StorageEvent("storage", { key, newValue: serializedValue as any, oldValue }));
    } catch (e) {
        console.error(`Failed to dispatch storage event for key "${key}":`, e);
    }
}

const isQuotaExceededError = (err: unknown): boolean =>
    err instanceof DOMException && (err.name === "QuotaExceededError" || err.name === "NS_ERROR_DOM_QUOTA_REACHED");

// Cached fetch entries store a `cachedAt` date, unlike plain values like "user" - evict the oldest ones first to free up space
const evictOldestCacheEntries = (skipKey: string) => {
    const candidates: { key: string, cachedAt: number }[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key || key === skipKey) continue;
        try {
            const item = window.localStorage.getItem(key);
            if (!item) continue;
            const parsed = JSON.parse(item);
            if (parsed && typeof parsed === "object" && "cachedAt" in parsed) {
                const timestamp = new Date(parsed.cachedAt).getTime();
                if (!isNaN(timestamp)) {
                    candidates.push({ key, cachedAt: timestamp });
                }
            }
        } catch {
            // not JSON, or not our cache shape - leave it alone
        }
    }
    candidates.sort((a, b) => a.cachedAt - b.cachedAt);
    return candidates;
};

// Hook
const getItem = typeof window.localStorage !== 'undefined' ? (key: string) => possiblyParse(window.localStorage.getItem(key)) : (key: string) => {
    return key in storage ? storage[key] : undefined
};
const setItem = typeof window.localStorage !== 'undefined' ? (key: string, value: unknown, withStorageUpdate = true): boolean => {
    const oldValue = window.localStorage.getItem(key);
    const serialized = JSON.stringify(value);
    try {
        window.localStorage.setItem(key, serialized);
        if (withStorageUpdate) dispatchStorageEvent(key, value, oldValue ?? undefined);
        return true;
    } catch (err) {
        if (!isQuotaExceededError(err)) throw err;
        // Storage is full - drop stale cached entries and try again before giving up
        const candidates = evictOldestCacheEntries(key);
        for (const candidate of candidates) {
            window.localStorage.removeItem(candidate.key);
            try {
                window.localStorage.setItem(key, serialized);
                if (withStorageUpdate) dispatchStorageEvent(key, value, oldValue ?? undefined);
                return true;
            } catch (retryErr) {
                if (!isQuotaExceededError(retryErr)) throw retryErr;
            }
        }
        // Exhausted all candidates, quota still exceeded
        console.warn(`localStorage quota exceeded, could not persist "${key}"`);
        return false;
    }
} : (key: string, value: unknown, withStorageUpdate = true): boolean => {
    const oldValue = storage[key];
    storage[key] = value;
    if (withStorageUpdate) dispatchStorageEvent(key, value, oldValue);
    return true;
};
const removeItem = typeof window.localStorage !== 'undefined' ? (key: string) => {
    const oldValue = window.localStorage.getItem(key);
    window.localStorage.removeItem(key)
    dispatchStorageEvent(key, undefined, oldValue ?? undefined);
} : (key: string) => {
    const oldValue = storage[key];
    delete storage[key];
    dispatchStorageEvent(key, undefined, oldValue);
};

export const getCurrentUser = () => {
    const user = getItem("user") as userTokens;
    return (typeof user === "undefined" ? null : user) as userTokens | null;
}
export const setCurrentUser = (user: userTokens): boolean => {
    return setItem("user", user);
}
export const logout = () => {
    removeItem("user");
    clearFetchCache();
}
// Drops every useFetchCached entry (anything shaped like { data, cachedAt }) without touching auth or other plain values.
export const clearFetchCache = () => {
   if (typeof window.localStorage === 'undefined') return;
    evictOldestCacheEntries("").forEach(candidate => window.localStorage.removeItem(candidate.key));
}

export const useLocalStorage = <T>(key: string):
    [T | undefined, React.Dispatch<T>, React.Dispatch<void>] => {
    const [value, setValue] = useState<T | undefined>(getItem(key) as T | undefined);
    // Sync to localstorage
    useEffect(() => {
        if (typeof value !== "undefined")
            // Since this window already had a storage event dispatched, 
            // there's no need to dispatch another (and it would cause an infinite loop)
            setItem(key, value, false);
    }, [key, value]);

    useEffect(() => {
        // Sync from localstorage
        const onStorage = (e: StorageEvent) => {
            // If the value of localstorage changed
            if (e.key === key) {
                // Update the state of the item
                setValue(getItem(key) as T);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [key]);

    return [value, setValue, () => { setValue(undefined); removeItem(key); }];
}